import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import api from '@/services/api';

interface Product {
  id?: string;
  name: string;
}

interface BankBranch {
  id: string;
  branchName: string;
  branchCode: string;
  address?: string;
  products: Product[];
}

interface Bank {
  id: string;
  name: string;
  shortCode: string;
  active: boolean;
  branchCount: number;
  branches: BankBranch[];
}

interface BanksState {
  items: Bank[];
  loading: boolean;
  deleting: string | null;
  error: string | null;
}

const initialState: BanksState = {
  items: [],
  loading: false,
  deleting: null,
  error: null,
};

export const fetchBanks = createAsyncThunk(
  'banks/fetchBanks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/v1/settings/banks');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch banks');
    }
  }
);

export const createBank = createAsyncThunk(
  'banks/createBank',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await api.post('/v1/settings/banks', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create bank');
    }
  }
);

export const updateBank = createAsyncThunk(
  'banks/updateBank',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/v1/settings/banks/${id}`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update bank');
    }
  }
);

export const deleteBank = createAsyncThunk(
  'banks/deleteBank',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/v1/settings/banks/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete bank');
    }
  }
);

export const createBranch = createAsyncThunk(
  'banks/createBranch',
  async ({ bankId, data }: { bankId: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/v1/settings/banks/${bankId}/branches`, data);
      return { bankId, branch: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create branch');
    }
  }
);

export const updateBranch = createAsyncThunk(
  'banks/updateBranch',
  async ({ branchId, bankId, data }: { branchId: string; bankId: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/v1/settings/branches/${branchId}`, data);
      return { bankId, branch: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update branch');
    }
  }
);

export const deleteBranch = createAsyncThunk(
  'banks/deleteBranch',
  async ({ branchId, bankId }: { branchId: string; bankId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/v1/settings/branches/${branchId}`);
      return { branchId, bankId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete branch');
    }
  }
);

const banksSlice = createSlice({
  name: 'banks',
  initialState,
  reducers: {
    addBank: (state, action) => {
      state.items.push(action.payload);
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBanks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBanks.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.items = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
      })
      .addCase(fetchBanks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createBank.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateBank.fulfilled, (state, action) => {
        const index = state.items.findIndex((b) => b.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteBank.pending, (state, action) => {
        state.deleting = action.meta.arg;
      })
      .addCase(deleteBank.fulfilled, (state, action) => {
        state.items = state.items.filter((b) => b.id !== action.payload);
        state.deleting = null;
      })
      .addCase(deleteBank.rejected, (state, action) => {
        state.deleting = null;
        state.error = action.payload as string;
      })
      .addCase(createBranch.fulfilled, (state, action) => {
        const bank = state.items.find(b => b.id === action.payload.bankId);
        if (bank) {
          bank.branches.push(action.payload.branch);
          bank.branchCount++;
        }
      })
      .addCase(updateBranch.fulfilled, (state, action) => {
        const bank = state.items.find(b => b.id === action.payload.bankId);
        if (bank) {
          const branchIndex = bank.branches.findIndex(br => br.id === action.payload.branch.id);
          if (branchIndex !== -1) {
            bank.branches[branchIndex] = action.payload.branch;
          }
        }
      })
      .addCase(deleteBranch.fulfilled, (state, action) => {
        const bank = state.items.find(b => b.id === action.payload.bankId);
        if (bank) {
          bank.branches = bank.branches.filter(br => br.id !== action.payload.branchId);
          bank.branchCount--;
        }
      });
  },
});

export const { addBank, setError } = banksSlice.actions;

const selectBanksState = (state: any) => state.banks;

export const selectBanks = createSelector(
  [selectBanksState],
  (state) => state.items
);

export const selectBanksDeleting = createSelector(
  [selectBanksState],
  (state) => state.deleting
);

export const selectBanksLoading = createSelector(
  [selectBanksState],
  (state) => state.loading
);

export const selectBanksError = createSelector(
  [selectBanksState],
  (state) => state.error
);

export default banksSlice.reducer;
